import { ListItemIcon, ListItemText, Menu, MenuItem } from '@utmost.co/utmost-design-system';
import { Tooltip, ActiveDiv, Icon, Divider, Typography } from '@beeline.com/bp-design-system';
import { useDarklyGlobal } from 'integrations/launchDarkly/legacy/useDarklyGlobal';
import { AxiosRequestConfig } from 'utils/gql/axios/AxiosClient';
import AppLink from 'common/AppLink/AppLink';
import { isSettingVisible, useStaticPermissions } from 'common/auth/Authorize';
import UserAvatar from 'common/avatars/UserAvatar';
import { HeaderAction } from 'common/header/HeaderAction';
import { useTaskDelegationQuery } from 'common/TaskDelegation/utils/useTaskDelegationQuery';
import { MixpanelEvents } from 'integrations/mixpanel/Events/MixpanelEvents';
import { useMixpanel } from 'integrations/mixpanel/useMixpanel';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'src/components/pages/routes/Routes.constants';
import { useAuth } from 'src/utils/hooks/data/useAuth';
import { useLoggedInUser } from 'src/utils/hooks/useLoggedInUser';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { post } from 'src/utils/rest-clients';
import styled from 'styled-components';
import { PagePathname } from 'utils/hooks/navigation/types';
import { useTenantCode } from 'utils/hooks/useTenantConfig';
import { beautifyName } from 'utils/textUtil';
import { Notifications } from './notifications/Notifications';
import headerJson from './translations/header.intl.json';
import { HelpMenu } from './HelpMenu';
interface Props {
  isHelpOpen: boolean;
  onHelpClick: () => void;
  onBriefingClick: () => void;
}
export const HeaderActions: FC<React.PropsWithChildren<Props>> = ({ isHelpOpen, onHelpClick, onBriefingClick }) => {
  const history = useHistory();
  const { signOut } = useAuth();
  const tenantCode = useTenantCode();
  const { showError, showSuccess } = useSnackbar();
  const authUser = useLoggedInUser()!;
  const [isOpen, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);
  const { track } = useMixpanel();
  const { noInitialTotalCount = false } = useDarklyGlobal();
  const handleMenu = (event: any) => {
    setOpen(true);
    setAnchorEl(event.currentTarget);
    track(MixpanelEvents.general.APP_HEADER_AVATAR_BUTTON_CLICK);
  };
  const handleClose = () => {
    setOpen(false);
    setAnchorEl(undefined);
  };
  const { totalCount } = useTaskDelegationQuery({
    variables: {
      page: { limit: 0, offset: 0 },
      query: { statuses: ['ACTIVE'] }
    },
    myAccount: true,
    count: true
  });
  const intl = useIntl();
  const getUrl = useCallback(
    (route: ROUTES): PagePathname => {
      switch (route) {
        case ROUTES.PROFILE: {
          return `/${ROUTES.PROFILE}/${authUser.person.personUid}`;
        }
        case ROUTES.MY_ACCOUNT: {
          return `/${ROUTES.MY_ACCOUNT}/`;
        }
        default: {
          return `/${ROUTES.SETTINGS}`;
        }
      }
    },
    [authUser.person.personUid]
  );
  const trackClick = useCallback(
    (route: ROUTES) => {
      const menuItem = beautifyName(route, '-');
      track(MixpanelEvents.general.APP_HEADER_AVATAR_MENU_ITEM_CLICK, { menuItem });
    },
    [track]
  );
  const isSettingEnabled = isSettingVisible(authUser);
  const staticPermission = useStaticPermissions();
  const myAccountEnabled = staticPermission({ permissionName: 'USER' }, true).READ;
  const noName = intl.formatMessage(headerJson.noName);
  const taskDelegationMessage = intl.formatMessage(headerJson.taskDelegationMessage);
  const taskDelegationBtnMessage = intl.formatMessage(headerJson.taskDelegationBtnMessage);
  const userName = authUser?.person?.name?.fullName ? authUser?.person?.name?.fullName : noName;
  const proxyingUser = authUser?.proxyingUser;
  const isLoggedInAsAnotherUser = !!proxyingUser;
  const proxyingUsername = proxyingUser?.person?.name?.fullName ?? noName;
  const handleUnproxyUser = useCallback(async () => {
    const config: AxiosRequestConfig = {
      headers: {
        'content-type': 'application/vnd.salteese.oc.v1.0+json',
        accept: 'application/vnd.salteese.oc.v1.0+json'
      }
    };
    try {
      const resp = await post<any>(`auth/unproxy`, {}, config);
      if (resp?.status === 200) {
        const path = `/${tenantCode}/myutmost`;
        history.push(path);
        history.go(0);
      }
    } catch (error) {
      showError(intl.formatMessage(headerJson.endProxyingFailed, { personName: userName }));
    }
  }, [showError, tenantCode, history, intl, userName]);
  useEffect(() => {
    if (isLoggedInAsAnotherUser) {
      showSuccess(
        intl.formatMessage(headerJson.loginAsUserSuccess, {
          personName: userName ?? noName
        })
      );
    }
  }, [userName, noName, intl, showSuccess, isLoggedInAsAnotherUser]);
  const proxyingCopy = userName ? intl.formatMessage(headerJson.actingAs, { personName: userName }) : noName;
  const tooltip = (
    <div>
      {isLoggedInAsAnotherUser
        ? intl.formatMessage(headerJson.avatarToolipProxying, {
            proxyingUsername,
            proxiedUsername: userName
          })
        : userName}
    </div>
  );
  const tasksBeingDelegated = !!totalCount;
  let proxyElm: JSX.Element | null = null;
  if (isLoggedInAsAnotherUser) {
    proxyElm = (
      <StyledActingAs data-test="acting-as">
        <Typography variant="body1">{proxyingCopy}</Typography>
      </StyledActingAs>
    );
  } else if (tasksBeingDelegated) {
    proxyElm = (
      <>
        <StyledActingAs data-test="tasks-being-delegated">
          <Typography variant="body1">{taskDelegationMessage}</Typography>
        </StyledActingAs>
        <AppLink className="delegate-link" url="/my-account/task-delegations">
          {taskDelegationBtnMessage}
        </AppLink>
      </>
    );
  }
  return (
    <>
      <HelpMenu isHelpOpen={isHelpOpen} onHelpClick={onHelpClick} onBriefingClick={onBriefingClick} />
      <Notifications v2Mappings={noInitialTotalCount} />
      <StyledAvatarLink $isLoggedInAsAnotherUser={isLoggedInAsAnotherUser} $tasksBeingDelegated={tasksBeingDelegated}>
        <Tooltip title={tooltip} placement="bottom">
          {proxyElm}
          <StyledAvatarWrapper
            aria-label=""
            aria-owns={isOpen ? 'menu-appbar' : undefined}
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
            data-test="user-button"
            className="appheaderactions__button appheaderactions__button--user"
          >
            <UserAvatar
              user={{
                fullName: authUser.person?.name?.fullName,
                profilePhoto: authUser.person.profilePhoto,
                personUid: authUser.person.personUid
              }}
              size={32}
            />
          </StyledAvatarWrapper>
        </Tooltip>
      </StyledAvatarLink>
      <Menu
        id="menu-appbar"
        dataTest="menu-appbar-popover"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        getContentAnchorEl={undefined}
        open={isOpen}
        onClose={handleClose}
        PaperProps={{
          style: paperStyles
        }}
      >
        <StyledMenuHeader dataTest="my-profile-header" disabled>
          <ListItemIcon>
            <StyledUserAvatar
              user={{
                profilePhoto: authUser.person.profilePhoto,
                personUid: authUser.person.personUid,
                fullName: authUser.person?.name?.fullName
              }}
              size={24}
            />
          </ListItemIcon>
          <ListItemText primary={<Typography variant="label1">{userName}</Typography>} />
        </StyledMenuHeader>
        <HeaderAction
          dataTest="my-profile-link"
          text={<FormattedMessage id="headermenu.myProfile.label" />}
          to={`${getUrl(ROUTES.PROFILE)}/details`}
          onClick={() => {
            handleClose();
            trackClick(ROUTES.PROFILE);
          }}
        />
        {myAccountEnabled && (
          <HeaderAction
            dataTest="my-account-link"
            text={<FormattedMessage id="headermenu.myAccount.label" />}
            to={getUrl(ROUTES.MY_ACCOUNT)}
            onClick={() => {
              handleClose();
              trackClick(ROUTES.MY_ACCOUNT);
            }}
          />
        )}
        {isSettingEnabled && (
          <HeaderAction
            dataTest="settings-link"
            text={<FormattedMessage id="headermenu.settings.label" />}
            to={getUrl(ROUTES.SETTINGS)}
            onClick={() => {
              handleClose();
              trackClick(ROUTES.SETTINGS);
            }}
            icon={<Icon name="settings" />}
          />
        )}
        {isLoggedInAsAnotherUser && (
          <div>
            <StyledDivider className="divider" />
            <HeaderAction
              dataTest="stop_proxying_button"
              text={<FormattedMessage id="headermenu.stopProxying.label" values={{ proxiedUsername: userName }} />}
              onClick={() => {
                handleClose();
                handleUnproxyUser();
              }}
            />
          </div>
        )}
        <StyledDivider className="divider" />
        <HeaderAction
          dataTest="signout-button"
          text={<FormattedMessage id="headermenu.signout.label" />}
          onClick={() => {
            handleClose();
            signOut();
          }}
        />
      </Menu>
    </>
  );
};
const StyledAvatarWrapper = styled(ActiveDiv)`
  border-radius: 50%;
  border: 1px solid transparent;
  transition: all 0.1s ease-in-out;

  &:focus,
  &:active {
    border-color: ${({ theme }) => theme.colorPrimary};
  }
`;
const paperStyles: React.CSSProperties = {
  padding: `0 0.5rem`
};
const StyledDivider = styled(Divider)`
  && {
    margin: 0.5rem 0;
  }
`;
const StyledMenuHeader = styled(MenuItem)`
  && {
    &.Mui-disabled {
      opacity: 1;
    }

    border: 1px solid ${({ theme }) => theme.colorSurfaceVariant};
    padding: 0.5rem 1rem;
    border-radius: 4px;
    margin-bottom: 0.5rem;
  }
`;
const StyledUserAvatar = styled(UserAvatar)`
  && {
    cursor: default;
    margin: 0 0.5rem 0 0;
  }
`;
export default HeaderActions;
const StyledActingAs = styled.div`
  white-space: nowrap;
  display: flex;
  align-items: center;
  margin-left: 0.75rem;
`;
interface StyledProps {
  $isLoggedInAsAnotherUser?: boolean;
  $tasksBeingDelegated?: boolean;
}
const StyledAvatarLink = styled.div<StyledProps>`
  .delegate-link {
    font-size: 14px;
    line-height: 24px;
  }

  padding: ${({ theme }) => theme.spacingSize.ml};
  ${(props) =>
    (props.$isLoggedInAsAnotherUser || props.$tasksBeingDelegated) &&
    `
    > span {
      display: flex;
      flex-direction: row;
      gap: 0.75rem;
      background: ${props.$isLoggedInAsAnotherUser ? '#e9dcff' : '#EBF5FF'};
      border-radius: 50px;
      padding: 0.25rem;
    }
  `};
`;
